// User routes with local JWT authentication
/* eslint-disable react-refresh/only-export-components -- route config module, exports no component itself */
import { lazy } from "react";
import { Navigate, RouteObject } from "react-router-dom";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { BusinessDataRoute } from "./components/BusinessDataRoute";

// Pages
const Login = lazy(() => import("./pages/auth/Login"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const SetPassword = lazy(() => import("./pages/auth/SetPassword"));
const Account = lazy(() => import("./pages/Account"));
const App = lazy(() => import("./pages/App"));
const Customers = lazy(() => import("./pages/Customers"));
const Orders = lazy(() => import("./pages/Orders"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const Portal = lazy(() => import("./pages/Portal"));
const PortalLogin = lazy(() => import("./pages/portal/AccountPages").then((m) => ({ default: m.PortalLogin })));
const PortalForgot = lazy(() => import("./pages/portal/AccountPages").then((m) => ({ default: m.PortalForgot })));
const PortalReset = lazy(() => import("./pages/portal/AccountPages").then((m) => ({ default: m.PortalReset })));
const PortalActivate = lazy(() => import("./pages/portal/AccountPages").then((m) => ({ default: m.PortalActivate })));
const PortalTokens = lazy(() => import("./components/logs/PortalTokens"));
const Prices = lazy(() => import("./pages/Prices"));
const PriceOffer = lazy(() => import("./pages/PriceOffer"));
const OfferHistory = lazy(() => import("./pages/OfferHistory"));
const DemoTires = lazy(() => import("./pages/DemoTires"));
const DemoTireOffer = lazy(() => import("./pages/DemoTireOffer"));
const TireSets = lazy(() => import("./pages/TireSets"));
const ManualOrder = lazy(() => import("./pages/ManualOrder"));
const NewTireSet = lazy(() => import("./pages/NewTireSet"));
const Logs = lazy(() => import("./pages/Logs"));
const EmailAnalytics = lazy(() => import("./components/logs/EmailAnalytics"));
const Security2FA = lazy(() => import("./components/Security2FA"));
const SecuritySessions = lazy(() => import("./components/SecuritySessions"));
const MyTenant = lazy(() => import("./pages/MyTenant"));
const StorageLocations = lazy(() => import("./pages/StorageLocations"));
const TireChange = lazy(() => import("./pages/TireChange"));
const SeasonCheck = lazy(() => import("./pages/SeasonCheck"));
const Help = lazy(() => import("./pages/Help"));
const Changelog = lazy(() => import("./pages/Changelog"));

export const userRoutes: RouteObject[] = [
  // Public routes (authentication)
  { path: "/login", element: <Login /> },
  { path: "/auth/login", element: <Login /> },
  { path: "/auth/forgot-password", element: <ForgotPassword /> },
  { path: "/auth/reset-password", element: <ResetPassword /> },
  { path: "/auth/set-password", element: <SetPassword /> },

  // Public portal
  { path: "/portal", element: <Portal /> },
  // Fester Kundenzugang (E-Mail + Passwort), öffentlich und ohne Werkstatt-Login
  { path: "/portal/login", element: <PortalLogin /> },
  { path: "/portal/forgot", element: <PortalForgot /> },
  { path: "/portal/account/reset", element: <PortalReset /> },
  { path: "/portal/account/activate", element: <PortalActivate /> },
  
  // Protected routes
  {
    path: "/",
    element: (
      <BusinessDataRoute>
        <App />
      </BusinessDataRoute>
    )
  },
  {
    path: "/account",
    element: (
      <ProtectedRoute>
        <Account />
      </ProtectedRoute>
    )
  },
  {
    path: "/hilfe",
    element: (
      <ProtectedRoute>
        <Help />
      </ProtectedRoute>
    )
  },
  {
    path: "/changelog",
    element: (
      <ProtectedRoute>
        <Changelog />
      </ProtectedRoute>
    )
  },
  {
    path: "/customers",
    element: (
      <BusinessDataRoute>
        <Customers />
      </BusinessDataRoute>
    )
  },
  {
    // Alte Adresse (Lesezeichen): die Änderungswünsche liegen jetzt in Mein Mandant → Portal-Zugänge
    path: "/customer-change-requests",
    element: <Navigate to="/my-tenant?section=portal:requests" replace />
  },
  {
    path: "/tire-sets",
    element: (
      <BusinessDataRoute>
        <TireSets />
      </BusinessDataRoute>
    )
  },
  {
    path: "/tire-sets/order/:tireSetId",
    element: (
      <BusinessDataRoute>
        <ManualOrder />
      </BusinessDataRoute>
    )
  },
  {
    path: "/tire-sets/new",
    element: (
      <BusinessDataRoute>
        <NewTireSet />
      </BusinessDataRoute>
    )
  },
  {
    path: "/import",
    element: <Navigate to="/my-tenant" replace />
  },
  {
    path: "/orders",
    element: (
      <BusinessDataRoute>
        <Orders />
      </BusinessDataRoute>
    )
  },
  {
    path: "/orders/:orderId",
    element: (
      <BusinessDataRoute>
        <OrderDetail />
      </BusinessDataRoute>
    )
  },
  {
    path: "/prices",
    element: (
      <BusinessDataRoute>
        <Prices />
      </BusinessDataRoute>
    )
  },
  {
    path: "/prices/offer/:priceId",
    element: (
      <BusinessDataRoute>
        <PriceOffer />
      </BusinessDataRoute>
    )
  },
  {
    path: "/prices/history",
    element: (
      <BusinessDataRoute>
        <OfferHistory />
      </BusinessDataRoute>
    )
  },
  {
    path: "/demo-tires",
    element: (
      <BusinessDataRoute>
        <DemoTires />
      </BusinessDataRoute>
    )
  },
  {
    path: "/demo-tires/offer/:demoTireId",
    element: (
      <BusinessDataRoute>
        <DemoTireOffer />
      </BusinessDataRoute>
    )
  },
  {
    path: "/settings",
    element: <Navigate to="/my-tenant" replace />
  },
  {
    path: "/email-analytics",
    element: (
      <BusinessDataRoute>
        <EmailAnalytics />
      </BusinessDataRoute>
    )
  },
  {
    path: "/portal-tokens",
    element: (
      <BusinessDataRoute>
        <PortalTokens />
      </BusinessDataRoute>
    )
  },
  {
    path: "/logs",
    element: (
      <ProtectedRoute>
        <Logs />
      </ProtectedRoute>
    )
  },
  {
    path: "/security/2fa",
    element: (
      <ProtectedRoute>
        <Security2FA />
      </ProtectedRoute>
    )
  },
  {
    path: "/security/sessions",
    element: (
      <ProtectedRoute>
        <SecuritySessions />
      </ProtectedRoute>
    )
  },
  {
    path: "/my-tenant",
    element: (
      <BusinessDataRoute>
        <MyTenant />
      </BusinessDataRoute>
    )
  },
  {
    path: "/storage-locations",
    element: (
      <BusinessDataRoute>
        <StorageLocations />
      </BusinessDataRoute>
    )
  },
  {
    path: "/tire-change",
    element: (
      <BusinessDataRoute>
        <TireChange />
      </BusinessDataRoute>
    )
  },
  {
    path: "/saisonpruefung",
    element: (
      <BusinessDataRoute>
        <SeasonCheck />
      </BusinessDataRoute>
    )
  },
];